import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, ArrowRight, ShieldCheck, Hammer, Layers, ShieldAlert } from 'lucide-react'
import { fetchPageBySlug } from '../lib/cms'
import useMeta from '../hooks/useMeta'
import ContactForm from '../components/ContactForm'

export default function Forside() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      const data = await fetchPageBySlug('index')
      setPageData(data)
      setLoading(false)
    }
    loadData()
  }, [])

  // Set metadata
  useMeta({
    title: 'JP Stål og Rør ApS | Erhvervsløsninger i stål og rør',
    description: 'JP Stål og Rør ApS i Tølløse leverer professionelle stål- og rørløsninger til erhvervskunder. Kontakt virksomheden på telefon eller mail for en erhvervsaftale.',
    ogTitle: 'JP Stål og Rør ApS | Erhvervsløsninger i stål og rør',
    ogDescription: 'JP Stål og Rør ApS i Tølløse leverer professionelle stål- og rørløsninger til erhvervskunder. Kontakt virksomheden på telefon eller mail for en erhvervsaftale.',
    ogImage: 'https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/d1a201a2-613d-4546-aa68-2fafe952f02e.jpeg',
    canonicalUrl: 'https://jpstaalogroer.dk/'
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-silver-200 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  // Fallback images if CMS is empty (though we seeded it successfully)
  const images = pageData?.images || [
    "https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/d1a201a2-613d-4546-aa68-2fafe952f02e.jpeg",
    "https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/68324731-ed12-4ac9-a74f-439c7a30c8ed.jpg",
    "https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/4528736c-cb84-4ce1-b39d-ed554f51130c.jpg",
    "https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/d3a0afd4-2ab6-4677-a8a2-c878fe873b0c.jpg"
  ]

  return (
    <div className="bg-silver-200 text-slate-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-silver-300 border-b border-silver-400 py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <img
            src={images[0]}
            alt="Smedearbejde"
            className="w-full h-full object-cover filter grayscale contrast-125"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-silver-200 via-silver-300/80 to-silver-200" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-700 text-xs font-bold uppercase tracking-wider mb-8 shadow-sm">
            <ShieldAlert className="w-4 h-4 shrink-0 text-amber-600" />
            <span>Udelukkende Erhverv &amp; Industri (B2B)</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-950 uppercase max-w-4xl mx-auto leading-tight">
            JP Stål og Rør – Specialister i svejsning af fjernvarmerør
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-700 max-w-2xl mx-auto font-medium">
            Professionelt smedearbejde, certifikatsvejsning og muffemontering udført med højeste faglighed siden 2006. Vi samarbejder kun med erhvervskunder.
          </p>

          {/* Large Heavy CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-xl mx-auto">
            <a
              href="tel:+4524460485"
              className="w-full sm:w-auto flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-wider text-sm px-5 py-3 rounded-sm transition-colors duration-200 shadow-lg cursor-pointer"
            >
              <span>(+45) 24 46 04 85</span>
            </a>
            <a
              href="mailto:jp@jpstaal.dk"
              className="w-full sm:w-auto flex items-center justify-center bg-silver-400 hover:bg-silver-500 text-slate-950 border border-silver-500 font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-sm transition-colors duration-200 shadow-md cursor-pointer"
            >
              <span>jp@jpstaal.dk</span>
            </a>
          </div>
        </div>
      </section>

      {/* B2B Target Notice Section */}
      <section className="bg-amber-500/10 border-b border-silver-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-500/20 rounded-sm text-amber-700 shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-950 uppercase tracking-wider">100% Erhvervsrettet Smedefirma</h3>
              <p className="text-sm text-slate-700 mt-1">
                Vi arbejder udelukkende som underleverandør for entreprenører, fjernvarmeselskaber, industrien og professionelle samarbejdspartnere. Vi tager ikke imod private henvendelser.
              </p>
            </div>
          </div>
          <div className="text-xs font-bold text-slate-600 uppercase tracking-widest whitespace-nowrap">
            CVR: 40160140
          </div>
        </div>
      </section>

      {/* Presentation Section */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-amber-600">Hvem er vi</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-950 uppercase tracking-tight mt-2 mb-6">
              Kvalitetsbevidst smed med god service siden 2006
            </h2>
            <div className="space-y-4 text-slate-700 leading-relaxed font-normal">
              <p>
                Har du brug for svejsning af fjernvarmerør? Hos JP Stål og Rør, er jeg en kvalitetbevidst smed med god service siden 2006. Høj faglighed og stor erfaring er et grundelement i alt arbejde udført af mit smedefirma.
              </p>
              <p>
                I min virksomhed udføres meget arbejde som underleverandør. Mange opgaver løses for kunder, som løbende vender tilbage med nye opgaver. Det tager jeg her i smedevirksomheden som et tegn på, at jeg til stadighed leverer godt, stabilt håndværk, og at jeg leverer det til tiden.
              </p>
              <p>
                Opgaverne varierer, dog med hyppighed i svejsning af fjernvarmerør. Mine kompetencer spænder også over certifikatsvejsning samt TIG-svejsning.
              </p>
            </div>
            <div className="mt-8">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 font-bold uppercase tracking-wider text-sm transition-colors duration-200 cursor-pointer"
              >
                <span>Kontakt mig for et uforpligtende tilbud</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Selected Images Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="aspect-square bg-silver-300 border border-silver-400 rounded-sm overflow-hidden shadow-md">
                <img
                  src={images[0]}
                  alt="Svejsning af fjernvarmerør og muffemontage"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
              <div className="aspect-[4/3] bg-silver-300 border border-silver-400 rounded-sm overflow-hidden shadow-md">
                <img
                  src={images[1]}
                  alt="Udtag til fjernvarme"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            </div>
            <div className="space-y-4 pt-8">
              <div className="aspect-[4/3] bg-silver-300 border border-silver-400 rounded-sm overflow-hidden shadow-md">
                <img
                  src={images[2]}
                  alt="Svejsning af fjernvarmerør"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
              <div className="aspect-square bg-silver-300 border border-silver-400 rounded-sm overflow-hidden shadow-md">
                <img
                  src={images[3]}
                  alt="Nedlægning af fjernvarme"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Teaser Section */}
      <section className="bg-silver-100 border-t border-b border-silver-400 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-amber-600">Ydelser</span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-950 uppercase tracking-tight mt-2">
              Hvad vi tilbyder erhvervskunder
            </h2>
            <p className="text-slate-700 mt-4">
              Vi leverer specialiserede løsninger inden for rør- og stålarbejde med fokus på præcision og holdbarhed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-silver-200 border border-silver-400 p-8 rounded-sm flex flex-col justify-between shadow-md">
              <div>
                <div className="p-3 bg-amber-500/20 text-amber-700 rounded-sm w-fit mb-6">
                  <Hammer className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-950 uppercase tracking-wider mb-4">Certifikatsvejsning</h3>
                <p className="text-slate-700 text-sm leading-relaxed mb-6">
                  Vi tilbyder certifikatsvejsning til de højeste krav såsom til rustfrit stål, fødevareindustrien og Pharma-industrien, hvor der oftest er efterspørgsel på TIG-svejsning.
                </p>
              </div>
              <Link
                to="/certifikatsvejsning"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700 transition-colors duration-200 mt-4 cursor-pointer"
              >
                <span>Læs mere</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Service 2 */}
            <div className="bg-silver-200 border border-silver-400 p-8 rounded-sm flex flex-col justify-between shadow-md">
              <div>
                <div className="p-3 bg-amber-500/20 text-amber-700 rounded-sm w-fit mb-6">
                  <Layers className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-950 uppercase tracking-wider mb-4">Fjernvarmesvejsning</h3>
                <p className="text-slate-700 text-sm leading-relaxed mb-6">
                  Certifikat TIG-svejsning af fjernvarmerør til de højeste krav. Vi sikrer, at rørledningerne er tæt forbundne og kan modstå højt tryk og temperaturvariationer.
                </p>
              </div>
              <Link
                to="/fjernvarme-svejsning-montering"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700 transition-colors duration-200 mt-4 cursor-pointer"
              >
                <span>Læs mere</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Service 3 */}
            <div className="bg-silver-200 border border-silver-400 p-8 rounded-sm flex flex-col justify-between shadow-md">
              <div>
                <div className="p-3 bg-amber-500/20 text-amber-700 rounded-sm w-fit mb-6">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-950 uppercase tracking-wider mb-4">Muffemontering</h3>
                <p className="text-slate-700 text-sm leading-relaxed mb-6">
                  Vi udfører muffemontering med en række certifikater inden for feltet, herunder Isoplus (krympemuffer, svejsemuffer) og Logstor (EW- &amp; BandJoint).
                </p>
              </div>
              <Link
                to="/muffemontering"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700 transition-colors duration-200 mt-4 cursor-pointer"
              >
                <span>Læs mere</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/jeg-tilbyder"
              className="inline-flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-wider text-sm px-8 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-md"
            >
              Se alle ydelser
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Details */}
          <div className="space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-amber-600">Kontakt</span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-950 uppercase tracking-tight mt-2 mb-4">
                Lad os drøfte dit næste erhvervsprojekt
              </h2>
              <p className="text-slate-700 leading-relaxed">
                Står du og mangler en kyndig smed til svejsning af fjernvarmerør, certifikatsvejsning eller muffemontering? Kontakt os i dag for en uforpligtende snak eller et tilbud.
              </p>
            </div>

            <div className="bg-silver-100 border border-silver-400 p-6 rounded-sm space-y-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Direkte kontaktveje</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a href="tel:+4524460485" className="flex items-center gap-3 p-4 bg-white border border-silver-400 hover:border-amber-500 transition-colors duration-200 rounded-sm shadow-xs">
                  <Phone className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-[10px] text-slate-600 uppercase tracking-wider font-bold">Jakob Petersen</p>
                    <p className="text-sm font-bold text-slate-950 whitespace-nowrap">(+45) 24 46 04 85</p>
                  </div>
                </a>
                <a href="mailto:jp@jpstaal.dk" className="flex items-center gap-3 p-4 bg-white border border-silver-400 hover:border-amber-500 transition-colors duration-200 rounded-sm shadow-xs">
                  <Mail className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-[10px] text-slate-600 uppercase tracking-wider font-bold">E-mail (Tilbud)</p>
                    <p className="text-sm font-bold text-slate-950 whitespace-nowrap">jp@jpstaal.dk</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="space-y-2 text-sm text-slate-700">
              <p><strong>JP Stål og Rør ApS</strong></p>
              <p>Tjørnedevej 127, 4340 Tølløse</p>
              <p>CVR-nr: 40160140</p>
              <p className="text-xs text-slate-600 mt-4">
                * Bemærk: Vi besvarer kun henvendelser fra registrerede virksomheder, forsyningsselskaber og offentlige instanser.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  )
}
